import { useEffect, useRef, useState } from "react";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import {
  SumsubBlockState,
  SumsubBlockAnswer,
  MismatchInfo,
  MismatchReason,
} from "@/pages/quiz/base/questions/sumsubAttachmentBlock/types";
import {
  useVerifySsn,
  VerifyParams,
  VerifyOutcome,
} from "@/pages/quiz/base/questions/sumsubAttachmentBlock/useVerifySsn";
import { SsnLast4State } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/ssnLast4State";
import { MismatchState } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/mismatchState";
import { HandoffState } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/handoffState";
import { SumsubState } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/sumsubState";
import { DEFAULT_VOUCHED_ATTEMPTS } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/common";

// Set once the user is sent to Sumsub after running out of SSN attempts. While
// present, the SSN option is hidden and the block opens straight to Sumsub.
const SUMSUB_FORCED_KEY = "vouched:sumsub-forced";
const isSumsubForced = () =>
  sessionStorage.getItem(SUMSUB_FORCED_KEY) === "true";

const GENERAL_ERROR_MESSAGE =
  "We couldn’t complete the check right now. Please try again or verify with a photo ID.";

const SumsubAttachmentBlock = (props: QuizQuestionProps<SumsubBlockAnswer>) => {
  const { verify, isLoading } = useVerifySsn();

  // Once sent to Sumsub after a failure, stay there across re-renders / refresh.
  const [isForced, setIsForced] = useState(() => isSumsubForced());
  // No verification attempts left on the order -> open straight to handoff.
  const noAttemptsLeft = props.question.attributes.vouched_attempts_left === 0;
  const [state, setState] = useState<SumsubBlockState>(() => {
    if (isForced) return SumsubBlockState.SUMSUB;
    if (noAttemptsLeft) return SumsubBlockState.HANDOFF;
    return SumsubBlockState.SSN_LAST_4;
  });
  const [ssnLast4, setSsnLast4] = useState("");
  const [verifyError, setVerifyError] = useState("");
  // Whether at least one verification attempt has failed (drives where the SSN
  // switch link returns to).
  const [hasFailedAttempt, setHasFailedAttempt] = useState(false);
  // SSN attempts the order has left. Seeded from the question, then kept in sync
  // with what each verify response reports (drives the forced flag).
  const [attemptsLeft, setAttemptsLeft] = useState(
    props.question.attributes.vouched_attempts_left ?? DEFAULT_VOUCHED_ATTEMPTS
  );
  const [mismatchInfo, setMismatchInfo] = useState<MismatchInfo>({
    reason: MismatchReason.DETAILS,
  });

  // Smoothly scroll the section into view (so the header/error is visible).
  const sectionRef = useRef<HTMLDivElement>(null);
  const didMountRef = useRef(false);
  // Deferred to the next frame so it runs after React commits (e.g. validation
  // errors), which the synchronous click-handler timing would otherwise drop.
  const scrollToTop = () =>
    requestAnimationFrame(() =>
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );

  // Scroll on state switch (skip the initial mount).
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    scrollToTop();
  }, [state]);

  const onSuccess = (answer: SumsubBlockAnswer) =>
    props.setQuestionAnswer(answer, false, true);

  const routeOutcome = (outcome: VerifyOutcome) => {
    if (outcome.kind === "success") {
      onSuccess({ passed: true, method: "ssn" });
      return;
    }

    // Any non-success is a failed attempt.
    setHasFailedAttempt(true);
    if (outcome.kind === "rateLimited") {
      setAttemptsLeft(0);
    } else if ("attemptsLeft" in outcome && outcome.attemptsLeft !== undefined) {
      setAttemptsLeft(outcome.attemptsLeft);
    }

    // Rate limited (429), or out of attempts (error/mismatch) -> photo-ID handoff.
    if (
      outcome.kind === "rateLimited" ||
      (outcome.kind === "mismatch" && outcome.attemptsLeft <= 0) ||
      (outcome.kind === "error" && outcome.attemptsLeft === 0)
    ) {
      setState(SumsubBlockState.HANDOFF);
      return;
    }

    // Any "error" status -> go to the correction screen; a rejected phone number
    // lands there too, with its own banner. Clear the SSN on every retry so the
    // user re-enters it (on the SSN screen and the mismatch screen).
    if (outcome.kind === "mismatch" || outcome.kind === "invalidPhone") {
      setSsnLast4("");
      setMismatchInfo({
        submitted: outcome.submitted,
        reason:
          outcome.kind === "invalidPhone"
            ? MismatchReason.INVALID_PHONE
            : MismatchReason.DETAILS,
      });
      setState(SumsubBlockState.MISMATCH);
      return;
    }

    // technical_error / thrown -> stay on the current screen with an inline error.
    setVerifyError(GENERAL_ERROR_MESSAGE);
  };

  const runVerify = async (params: VerifyParams) => {
    setVerifyError("");
    const outcome = await verify(params);
    routeOutcome(outcome);
  };

  const handleSsnSubmit = (ssn: string) => runVerify({ ssnLastFour: ssn });

  const handleCorrectionSubmit = (
    corrections: Omit<VerifyParams, "ssnLastFour">
  ) => runVerify({ ssnLastFour: ssnLast4, ...corrections });

  // Photo-ID link. Only once the SSN attempts are exhausted, remember the
  // handoff so the block stays on Sumsub — hides the SSN option and reopens
  // Sumsub on refresh. Until then the user can switch back to the SSN path.
  const goToSumsub = () => {
    if (attemptsLeft <= 0 || noAttemptsLeft) {
      sessionStorage.setItem(SUMSUB_FORCED_KEY, "true");
      setIsForced(true);
    }
    setState(SumsubBlockState.SUMSUB);
  };

  // Back from Sumsub: after a failure, return to the correction screen.
  const goToSsnPath = () =>
    setState(
      hasFailedAttempt ? SumsubBlockState.MISMATCH : SumsubBlockState.SSN_LAST_4
    );

  const renderState = () => {
    switch (state) {
      case SumsubBlockState.SSN_LAST_4:
        return (
          <SsnLast4State
            value={ssnLast4}
            isLoading={isLoading}
            verifyError={verifyError}
            onChange={setSsnLast4}
            onSubmit={handleSsnSubmit}
            onUploadInstead={goToSumsub}
            onScrollToTop={scrollToTop}
          />
        );
      case SumsubBlockState.MISMATCH:
        return (
          <MismatchState
            info={mismatchInfo}
            isLoading={isLoading}
            verifyError={verifyError}
            ssnValue={ssnLast4}
            onSsnChange={setSsnLast4}
            onSubmit={handleCorrectionSubmit}
            onUploadInstead={goToSumsub}
            onScrollToTop={scrollToTop}
          />
        );
      case SumsubBlockState.HANDOFF:
        return <HandoffState onContinue={goToSumsub} />;
      case SumsubBlockState.SUMSUB:
        return (
          <SumsubState
            {...props}
            hideSsnSwitch={isForced || noAttemptsLeft}
            onUploadSsn={goToSsnPath}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div ref={sectionRef} className="scroll-mt-4">
      {/* key change re-triggers the fade-in on every state transition */}
      <div key={state} className="animate-fade-in-500">
        {renderState()}
      </div>
    </div>
  );
};

export { SumsubAttachmentBlock };
